import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, Bell, User, LogOut, TrendingUp, Home, LifeBuoy } from 'lucide-react';
import PrayingHands from '@/components/icons/PrayingHands';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { HelpSupportSheet } from '@/components/HelpSupportSheet';

const TopNav = ({ user, onLogout, unreadCount = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [helpOpen, setHelpOpen] = useState(false);

  if (!user) return null;

  const hideOnPaths = ['/', '/onboarding', '/auth'];
  if (hideOnPaths.includes(currentPath)) return null;

  const isActive = (paths) => paths.some(p => currentPath.startsWith(p));

  const getPageName = () => {
    if (currentPath.startsWith('/pray-today')) return 'Pray';
    if (currentPath.startsWith('/prayer-rising')) return 'Movement';
    if (currentPath.startsWith('/communities')) return 'Groups';
    if (currentPath.startsWith('/plan')) return 'My plan';
    if (currentPath.startsWith('/pricing')) return 'Plans';
    if (currentPath.startsWith('/notifications')) return 'Notifications';
    if (currentPath.startsWith('/profile')) return 'Profile';
    if (currentPath.startsWith('/admin')) return 'Admin';
    if (currentPath.startsWith('/messages')) return 'Messages';
    if (currentPath.startsWith('/resources')) return 'Resources';
    if (currentPath.includes('/submit')) return 'New Prayer';
    if (currentPath.includes('/request/')) return 'Prayer Detail';
    return 'Journal';
  };

  const navItems = [
    {
      path: '/pray-today',
      label: 'Pray',
      customIcon: true,
      matchPaths: ['/pray-today'],
    },
    {
      path: '/communities',
      label: 'Groups',
      icon: Users,
      matchPaths: ['/communities', '/plan', '/pricing'],
    },
    {
      path: '/prayer-rising',
      label: 'Movement',
      icon: TrendingUp,
      matchPaths: ['/prayer-rising'],
    },
    {
      path: '/notifications',
      label: null,
      icon: Bell,
      matchPaths: ['/notifications'],
    },
  ];

  return (
    <>
    <nav
      className="hidden md:block sticky top-0 bg-white/80 backdrop-blur-md border-b border-[#E8E6DF]/40 z-50"
      data-testid="top-nav"
    >
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        {/* Left: Home icon + Page name */}
        <div className="flex items-center gap-2">
          <Link
            to="/requestor"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-[#5C6666] hover:text-[#4E8B7C] hover:bg-[#4E8B7C]/10 transition-colors"
            data-testid="topnav-home-link"
          >
            <Home size={18} />
          </Link>
          <span className="text-[#E8E6DF]">|</span>
          <span className="text-sm font-medium text-[#2C3333]">{getPageName()}</span>
        </div>

        {/* Right: Nav items */}
        <div className="flex items-center gap-1">
          {navItems.map((item) => {
            const active = isActive(item.matchPaths);
            const IconComponent = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? 'text-[#4E8B7C] bg-[#4E8B7C]/10'
                    : 'text-[#5C6666] hover:text-[#2C3333] hover:bg-[#F5F3EF]'
                }`}
                data-testid={`topnav-${item.label?.toLowerCase() || 'notifications'}`}
              >
                {item.customIcon ? (
                  <PrayingHands
                    size={18}
                    fill={active ? '#4E8B7C' : '#5C6666'}
                    color={active ? '#4E8B7C' : '#5C6666'}
                  />
                ) : (
                  <span className="relative">
                    <IconComponent size={18} strokeWidth={active ? 2.5 : 2} />
                    {item.path === '/notifications' && unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-[#D9534F] text-white text-[10px] font-bold rounded-full px-1" data-testid="topnav-notification-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </span>
                )}
                {item.label && (
                  <span className={`text-xs font-medium ${active ? 'text-[#4E8B7C]' : ''}`}>
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Profile dropdown with logout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive(['/profile'])
                    ? 'text-[#4E8B7C] bg-[#4E8B7C]/10'
                    : 'text-[#5C6666] hover:text-[#2C3333] hover:bg-[#F5F3EF]'
                }`}
                data-testid="topnav-profile-trigger"
              >
                {user.avatarUrl ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/avatar`}
                    alt=""
                    loading="lazy"
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <User size={18} />
                )}
                <span className="text-xs font-medium">
                  {user.name?.split(' ')[0] || 'Profile'}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onClick={() => navigate('/profile')}
                className="cursor-pointer"
                data-testid="topnav-profile-link"
              >
                <User size={14} className="mr-2" />
                Profile & Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setHelpOpen(true)}
                className="cursor-pointer"
                data-testid="topnav-help-link"
              >
                <LifeBuoy size={14} className="mr-2" />
                Help & Feedback
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onLogout}
                className="cursor-pointer text-[#D9534F] focus:text-[#D9534F]"
                data-testid="topnav-logout-btn"
              >
                <LogOut size={14} className="mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
    {/* Outside <nav> deliberately -- that element's backdrop-blur-md
        establishes a containing block for position:fixed descendants in
        modern browsers, which confined this sheet's overlay to the ~56px
        nav bar instead of the full viewport. */}
    <HelpSupportSheet open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default TopNav;
